import React, { useState, useEffect, useRef } from 'react';
import { MessageCircle, Send, Shield, X } from 'lucide-react';
import chatbot3 from "../assests/bot-2.png";

const ScrollTop = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isFooterVisible, setIsFooterVisible] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      type: 'bot',
      content: "Welcome to Kristellar Aerospace! I'm your expert in satellite technology, mission control systems, cybersecurity, and AI robotics. Ask me about our projects, advisory board, or how to stay updated with our latest developments!"
    }
  ]);
  const [inputMessage, setInputMessage] = useState('');
  const messagesEndRef = useRef(null);

  useEffect(() => {
    const toggleVisibility = () => {
      setIsScrolled(window.pageYOffset > 300);
      const footer = document.querySelector('footer');
      if (footer) {
        const rect = footer.getBoundingClientRect();
        setIsFooterVisible(rect.top < window.innerHeight);
      }
    };
    window.addEventListener('scroll', toggleVisibility);
    return () => window.removeEventListener('scroll', toggleVisibility);
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleChatClick = () => {
    setIsOpen(!isOpen);
  };

  const topicResponses = {
    'cubesat': "Our CubeSat projects focus on advancing small satellite technology for various applications. Our Mission Control Software provides comprehensive satellite monitoring, telemetry processing, and command execution capabilities, ensuring easy-to-use control access for any space mission.",
    'radio': "Our Software-Defined Radio (SDR) solutions for CubeSats leverage advanced FPGA technology for cutting-edge sensing and communication applications. We're revolutionizing space communications with dynamic frequency tuning and enhanced protocols.",
    'ucav': "The RiGEL UCAV platform delivers unparalleled ISR capabilities on our SWaP platform. Our mission control software enables autonomous flight, target tracking, and electromagnetic spectrum dominance for military operations.",
    'uav': "We're at the forefront of Unmanned Aerial Vehicle (UAV) technology development. Our projects focus on advancing drone technology through innovative solutions in autonomous control, navigation, and mission execution.",
    'agastya': "Agastya, our advanced DNN Robot, represents a decade of research in deep neural networks. It enables autonomous operation of drone and aircraft swarms without GPS or pilot input, revolutionizing space security through AI innovation.",
    'cybersecurity': "We conduct extensive research on malware, ransomware, and multi-threat attack vectors. Our cyber defense solutions include threat assessment, monitoring, and strategic execution of tactical cyber defense measures.",
    'company': "Kristellar Aerospace is a leading innovator in satellite design, integration, and mission support services. Our vision is to deliver innovative space and deep-space systems for advanced scientific discovery, while our mission focuses on pioneering microsatellite technology for a new space society.",
    'blog': "Our blog features expert insights from our distinguished advisory board members, including Mr. Kurt Blöchlinger, who brings over 30 years of Swiss government law enforcement experience, and Brigadier (Retd.) Dr. L C Patnaik, an expert in multi-dimensional warfare and military diplomacy.",
    'newsletter': "Stay updated with our latest developments by subscribing to our newsletter! You'll receive regular updates about our projects, technology advances, and company news. Visit our website to subscribe.",
    'contact': "You can reach us at: Phone: +91 6742915135, Email: support@kristellar.com, Address: OU-404, 4th Floor Esplanade One, Cuttack Puri Road, Bhubaneswar-10",
    'advisory': "Our advisory board includes distinguished experts like Mr. Kurt Blöchlinger, with over 30 years in Swiss law enforcement and defense, and Brigadier (Retd.) Dr. L C Patnaik, a decorated Infantry Officer specializing in multi-dimensional warfare and military diplomacy.",
    'default': "As a leader in space technology, we specialize in satellite systems, mission control software, cybersecurity, and AI robotics. How can I help you learn more about our services, projects, or latest developments?"
  };

  const generateResponse = (input) => {
    const lowercaseInput = input.toLowerCase();
    let response = topicResponses.default;
    Object.keys(topicResponses).forEach(key => {
      if (lowercaseInput.includes(key)) {
        response = topicResponses[key];
      }
    });
    return response;
  };

  const handleSendMessage = () => {
    if (inputMessage.trim() === '') return;

    setMessages(prev => [...prev, { type: 'user', content: inputMessage }]);

    setTimeout(() => {
      setMessages(prev => [...prev, { type: 'bot', content: generateResponse(inputMessage) }]);
    }, 500);

    setInputMessage('');
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSendMessage();
    }
  };

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  return (
    <div className="hidden sm:block fixed right-6 bottom-32 sm:bottom-10 text-center cursor-pointer overflow-hidden transition-all duration-300 ease-in-out z-[9999]">
      <div 
        className={`relative w-[100px] h-[70px] mt-12 transform transition-transform duration-300 animate-bounce hover:animate-none ${(isFooterVisible) ? 'translate-y-full opacity-0' : 'translate-y-0 opacity-100'}`} 
        onClick={handleChatClick}>
        <div className="absolute inset-0 flex items-center justify-center text-white">
          <img src={chatbot3} className="w-40 h-32 object-cover" alt="Chatbot" />
        </div>
      </div>

      {isOpen && (
        <div className="fixed bottom-4 right-4 w-96 h-[500px] bg-white rounded-lg shadow-xl flex flex-col">
          <div className="flex items-center justify-between p-4 bg-headerBlue text-white rounded-t-lg">
            <div className="flex items-center gap-2">
              <Shield className="w-5 h-5" />
              <span className="font-semibold">Kristellar Aerospace</span>
            </div>
            <button onClick={handleChatClick} className="hover:bg-blue-700 p-1 rounded">
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {messages.map((message, index) => (
              <div key={index} className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[80%] p-3 leading-tight text-start rounded-lg ${message.type === 'user' ? 'bg-headerBlue text-white' : 'bg-gray-100 text-gray-800'}`}>{message.content}</div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>
          <div className="p-4 border-t">
            <div className="flex gap-2">
              <input
                type="text"
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Ask about our projects, blog, newsletter, or contact info..."
                className="flex-1 p-2 border rounded-lg focus:outline-none focus:border-blue-600 text-black"
              />
              <button onClick={handleSendMessage} className="p-4 bg-headerBlue text-white rounded-lg hover:bg-blue-700 transition-colors">
                <Send className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      )}

      <div 
        className={`
          absolute top-0 left-0 w-10 h-10 transform transition-transform duration-300
          ${(isScrolled && isFooterVisible) ? 'translate-y-0 opacity-100' : '-translate-y-full opacity-0'}
        `}
        onClick={scrollToTop}
      >
        <div className="absolute inset-0 bg-headerBlue rounded transition-all duration-200 ease-in-out hover:bg-[#585252]" />
        <div className="absolute inset-0 flex items-center justify-center text-white">
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5 10l7-7m0 0l7 7m-7-7v18"
            />
          </svg>
        </div>
      </div>
    </div>
  );
};

export default ScrollTop;